/**
 * LiveDemoSection — landing-page section with three one-click demo personas
 * (Owner / Sales Rep / Customer). Calls POST /api/demo/session and drops the
 * visitor straight into a fully seeded "Legacy Detailing Demo" workspace.
 */
import { useState } from "react";
import api, { setAccessToken } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Handshake, Car, Loader2, PlayCircle, Sparkles } from "lucide-react";

const PERSONAS = [
  {
    key: "owner",
    icon: LayoutDashboard,
    title: "Business Owner",
    desc: "The full command center — 210 clients, 3 months of jobs, invoices, payment plans, leads, payroll & the Rep Academy.",
    accent: "from-cyan-500/15 to-transparent border-cyan-400/30 hover:border-cyan-300/60",
    chip: "text-cyan-300",
  },
  {
    key: "rep",
    icon: Handshake,
    title: "Sales Rep",
    desc: "Rep dashboard with live commissions, bookings, bonuses, academy courses & the rep marketplace.",
    accent: "from-orange-500/15 to-transparent border-orange-400/30 hover:border-orange-300/60",
    chip: "text-orange-300",
  },
  {
    key: "customer",
    icon: Car,
    title: "Customer Portal",
    desc: "What your clients see — service history, rewards, receipts, reschedule & pay-a-plan online.",
    accent: "from-emerald-500/15 to-transparent border-emerald-400/30 hover:border-emerald-300/60",
    chip: "text-emerald-300",
  },
];

export default function LiveDemoSection() {
  const [busy, setBusy] = useState(null);

  const launch = async (persona) => {
    setBusy(persona);
    try {
      const { data } = await api.post("/demo/session", { persona });
      setAccessToken(data.access_token);
      try { sessionStorage.setItem("ld_demo_tour", persona); } catch {}
      window.location.href = data.redirect;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo is waking up — try again in a minute.");
      setBusy(null);
    }
  };

  return (
    <section id="live-demo" className="border-t border-white/5 scroll-mt-20" data-testid="live-demo-section">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <span className="uppercase text-xs tracking-[0.2em] font-bold text-emerald-300/80 inline-flex items-center gap-2" data-testid="live-demo-eyebrow">
            <Sparkles size={13} /> No signup needed
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mt-3">Explore the live demo</h2>
          <p className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm md:text-base">
            One click drops you into a real workspace preloaded with 3 months of business —
            70 new clients a month, live schedules, payment plans and commissions. Look around,
            click anything. It resets itself every night.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PERSONAS.map((p) => {
            const Icon = p.icon;
            return (
              <button key={p.key} onClick={() => launch(p.key)} disabled={busy !== null}
                data-testid={`demo-card-${p.key}`}
                className={`text-left p-6 rounded-2xl border bg-gradient-to-b ${p.accent} transition-all hover:-translate-y-0.5 disabled:opacity-60`}>
                <Icon size={26} className={p.chip} />
                <div className="mt-4 font-heading font-bold text-lg text-white">{p.title}</div>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{p.desc}</p>
                <span className={`mt-4 inline-flex items-center gap-1.5 text-sm font-semibold ${p.chip}`}>
                  {busy === p.key ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
                  {busy === p.key ? "Opening demo…" : "Launch demo"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
